import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Clock, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

export function CampaignStatusPill({ status }) {
  const styles = {
    draft: "bg-zinc-100 text-zinc-700",
    scheduled: "bg-blue-50 text-[#002BF6] border border-blue-200",
    sending: "bg-amber-50 text-amber-700 border border-amber-200",
    sent: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    partial: "bg-amber-50 text-amber-700 border border-amber-200",
    failed: "bg-red-50 text-red-700 border border-red-200",
    cancelled: "bg-zinc-100 text-zinc-500 border border-zinc-200",
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${styles[status] || styles.draft}`}>
      {status}
    </span>
  );
}

export function DeliveryDots({ stats }) {
  const items = [
    { k: "sent",      color: "bg-zinc-400",    label: "sent" },
    { k: "delivered", color: "bg-emerald-500",  label: "delivered" },
    { k: "bounced",   color: "bg-amber-500",    label: "bounced" },
    { k: "failed",    color: "bg-red-500",      label: "failed" },
  ];
  return (
    <div className="flex items-center gap-3">
      {items.map((i) => (
        <div key={i.k} className="flex items-center gap-1" title={i.label}>
          <span className={`w-2 h-2 rounded-full ${i.color}`} />
          <span className="font-mono">{stats[i.k] || 0}</span>
        </div>
      ))}
    </div>
  );
}

export function CampaignsTab() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaigns", { params: { page, page_size: 25 } });
      setItems(data.items);
      setTotal(data.total);
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  return (
    <div data-testid="campaigns-tab">
      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-5 py-3 font-semibold">Sent / Scheduled</th>
              <th className="px-5 py-3 font-semibold">Name</th>
              <th className="px-5 py-3 font-semibold">Audience</th>
              <th className="px-5 py-3 font-semibold">Size</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold">Delivery</th>
              <th className="px-5 py-3 font-semibold w-1">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-zinc-500">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-zinc-500">No campaigns yet. Compose your first one →</td></tr>
            ) : (
              items.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50 cursor-pointer"
                  onClick={() => navigate(`/communication/campaigns/${c.id}`)}
                  data-testid={`campaign-row-${c.id}`}
                >
                  <td className="px-5 py-3.5 text-zinc-700 text-xs whitespace-nowrap">
                    {c.status === "scheduled" && c.scheduled_for ? (
                      <span className="inline-flex items-center gap-1 text-[#002BF6] font-semibold">
                        <Clock size={11} weight="bold" />
                        {new Date(c.scheduled_for).toLocaleString()}
                      </span>
                    ) : c.sent_at ? new Date(c.sent_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-5 py-3.5 font-medium text-zinc-900">
                    <div>{c.name}</div>
                    <div className="text-xs text-zinc-500 truncate max-w-[280px]">{c.subject}</div>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-[11px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700">
                      {c.audience}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 font-mono text-xs">{c.recipient_count}</td>
                  <td className="px-5 py-3.5"><CampaignStatusPill status={c.status} /></td>
                  <td className="px-5 py-3.5 text-xs text-zinc-600">
                    <DeliveryDots stats={c.stats || {}} />
                  </td>
                  <td className="px-5 py-3.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {c.status === "scheduled" && (
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs text-[#002BF6] hover:text-[#0021C7]"
                          onClick={async () => {
                            try {
                              await api.post(`/campaigns/${c.id}/send-now`);
                              toast.success("Sending now…");
                              load();
                            } catch (err) {
                              toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Send-now failed");
                            }
                          }}
                          data-testid={`send-now-${c.id}`}
                        >
                          <PaperPlaneTilt size={11} className="mr-1" /> Send now
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs text-red-600 hover:text-red-700"
                          onClick={async () => {
                            if (!window.confirm("Cancel this scheduled campaign?")) return;
                            try {
                              await api.post(`/campaigns/${c.id}/cancel`);
                              toast.success("Cancelled");
                              load();
                            } catch (err) {
                              toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Cancel failed");
                            }
                          }}
                          data-testid={`cancel-campaign-${c.id}`}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {total > 25 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <div>Page {page} of {Math.ceil(total / 25)} ({total} total)</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / 25)} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
